<template>
  <v-container>
    <v-card v-if="currentQuestion !== null" class="question-card">
      <!-- Barra de navegação superior -->
      <v-row class="justify-LEFT">
        <v-select
          class="ml-5 mt-5"
          v-model="currentQuestionIndexInput"
          :items="questionNumbers"
          style="max-width: 300px;"
          label="Selecione a Questão"
          dense
          outlined
        ></v-select>
      </v-row>

      <!-- Mensagem de pontuação -->
      <div v-if="scoreMessage" :class="scoreClass" class="score-message">
        {{ scoreDetails }}
        <span v-if="dbName !== 'AGSUS'"> - {{ scoreMessage }}</span>
      </div>

      <!-- Conteúdo da questão -->
      <v-card-title class="headline font-weight-bold" :key="currentQuestion.questionId">
        Questão {{ currentQuestion.questionId }} - 
        <span v-if="isCorrectAnswer" class="green--text">Correto</span>
        <span v-else class="red--text">Errada</span>
      </v-card-title>
      <v-card-text>
        <div class="question-container">
          <div class="question-text" :style="{ 'white-space': 'pre-line' }">
            <h4>{{ currentQuestion.question }}</h4>
            <v-col cols="12" class="d-flex justify-center">
              <div v-if="currentQuestion.questionImage" class="image-container">
                <img :src="currentQuestion.questionImage" alt="Question Image" class="question-image" />
              </div>
            </v-col>
            <v-radio-group 
              v-model="currentAnswer" 
              :mandatory="true" 
              :key="currentQuestion.questionId"
            >
              <v-radio
                v-for="(option, optionIndex) in currentQuestion.questionOptions"
                :key="optionIndex"
                :label="option"
                :value="option"
                :disabled="true"
                :class="{
                  'green-outline': isCorrectOption(option),
                  'red-outline': isWrongOption(option),
                }"
              ></v-radio>
            </v-radio-group>
          </div>
        </div>

        <!-- Card de Resolução igual ao QuestionsAnswers -->
        <v-card class="mt-5">
          <v-card-title class="headline">Resolução</v-card-title>
          <v-card-text>
            <div v-if="currentQuestion.questionAnswer !== null" class="mb-4">
              <strong>Resposta Correta:</strong>
              <p><strong class="green--text text--darken-2">{{ currentQuestion.questionAnswer }}</strong></p>
            </div>
            <div v-if="currentQuestion.questionComment !== null" class="mb-4" :style="{ 'white-space': 'pre-line' }">
              <strong>Comentário:</strong>
              <p>{{ currentQuestion.questionComment || 'Estamos elaborando a resolução. Em breve, você poderá encontrá-la aqui.' }}</p>
            </div>
            <div v-if="currentQuestion.questionVideo" class="mb-4">
              <strong>Vídeo:</strong>
              <v-responsive>
                <iframe width="100%" height="315" :src="currentQuestion.questionVideo" frameborder="0" allowfullscreen></iframe>
              </v-responsive>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="goToPreviousQuestion" color="primary" v-if="currentQuestionIndex > 0">
          Anterior
        </v-btn>
        <v-btn class="ml-auto" @click="goToNextQuestion" color="primary" v-if="currentQuestionIndex < questions.length - 1">
          Próxima
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
export default {
  props: {
    questions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentQuestionIndex: 0,
      currentQuestion: null,
      currentAnswer: null,
      currentQuestionIndexInput: 1, // Adicionado para o v-select
      scoreMessage: null, // Adicionado para exibir mensagem de pontuação
      scoreClass: null, // Adicionado para definir a classe da mensagem de pontuação
    };
  },
  computed: {
    questionNumbers() {
      return this.questions.length > 0
        ? Array.from({ length: this.questions.length }, (_, i) => i + 1)
        : [];
    },
    isCorrectOption() {
      return (option) => option === this.currentQuestion.questionAnswer;
    },
    isWrongOption() {
      return (option) => {
        // Verifica se a opção é a selecionada pelo usuário e não é a correta
        const optionIndex = this.currentQuestion.userAnswer.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.
        const userSelectedOption = this.currentQuestion.questionOptions[optionIndex];
        return option === userSelectedOption && option !== this.currentQuestion.questionAnswer;
      };
    },
    isCorrectAnswer() {
      if (!this.currentQuestion || !this.currentQuestion.userAnswer) return false;

      // Mapeia a letra (userAnswer) para a opção correspondente
      const optionIndex = this.currentQuestion.userAnswer.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.
      const userSelectedOption = this.currentQuestion.questionOptions[optionIndex];

      // Compara a opção selecionada pelo usuário com a resposta correta
      return userSelectedOption === this.currentQuestion.questionAnswer;
    },
    scorePercentage() {
      const correctAnswers = this.questions.filter((question) => {
        const optionIndex = question.userAnswer.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.
        const userSelectedOption = question.questionOptions[optionIndex];
        return userSelectedOption === question.questionAnswer;
      }).length;
      return (correctAnswers / this.questions.length) * 100;
    },
    scoreMessage() {
      if (this.scorePercentage >= 77.5) {
        return "Pontuação acima da nota de corte para esse simulado.";
      } else {
        return "Pontuação abaixo da nota de corte para esse simulado.";
      }
    },
    scoreClass() {
      return this.scorePercentage >= 77.5 ? "blue--text" : "orange--text";
    },
    scoreDetails() {
      const correctAnswers = this.questions.filter((question) => {
        const optionIndex = question.userAnswer.charCodeAt(0) - 97; // 'a' -> 0, 'b' -> 1, etc.
        const userSelectedOption = question.questionOptions[optionIndex];
        return userSelectedOption === question.questionAnswer;
      }).length;
      return `${correctAnswers}/${this.questions.length}`;
    },
    dbName() {
      return this.$store.getters.dbName;
    },
  },
  methods: {
    goToPreviousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
        this.updateCurrentQuestion();
      }
    },
    goToNextQuestion() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.currentQuestion = null; // Força a remoção do componente

        this.$nextTick(() => {
          this.currentQuestion = { ...this.questions[this.currentQuestionIndex] }; // Cópia reativa
          this.currentAnswer = this.questions[this.currentQuestionIndex].questionAnswer;
          this.currentQuestionIndexInput = this.currentQuestionIndex + 1; // Atualiza o índice para o v-select
        });
      }
    },
    updateCurrentQuestion() {
      this.currentQuestion = this.questions[this.currentQuestionIndex];
      this.currentAnswer = this.questions[this.currentQuestionIndex].questionAnswer;
      this.currentQuestionIndexInput = this.currentQuestionIndex + 1; // Atualiza o índice para o v-select

      this.$nextTick(() => {
        this.updateScoreMessage(); // Atualiza a mensagem de pontuação
      });
    },
    updateScoreMessage() {
      if (this.isCorrectAnswer) {
        this.scoreMessage = "Resposta correta!";
        this.scoreClass = "green--text";
      } else {
        this.scoreMessage = "Resposta errada!";
        this.scoreClass = "red--text";
      }
    },
  },
  watch: {
    currentQuestionIndexInput(newValue) {
      if (newValue >= 1 && newValue <= this.questions.length) {
        this.currentQuestionIndex = newValue - 1;
        this.updateCurrentQuestion();
      }
    },
  },
  mounted() {
    if (this.questions && this.questions.length > 0) {
      this.updateCurrentQuestion();
    }
  },
};
</script>

<style>
/* Reaproveite os estilos de QuestionsAnswers */
.question-card {
  background-color: #f5f5f5;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.green-outline {
  border: 2px solid green; /* Adiciona uma borda verde sólida */
  color: green; /* Define o texto como verde */
  font-weight: bold; /* Deixa o texto mais destacado */
}

.red-outline {
  border: 2px solid red; /* Adiciona uma borda vermelha sólida */
  color: red; /* Define o texto como vermelho */
  font-weight: bold; /* Deixa o texto mais destacado */
}

.question-image {
  max-width: 60%;
  height: auto;
  display: block;
}

.green--text {
  color: green;
  font-weight: bold;
}

.red--text {
  color: red;
  font-weight: bold;
}

.blue--text {
  color: blue;
  font-weight: bold;
}

.orange--text {
  color: orange;
  font-weight: bold;
}

.v-radio {
  opacity: 1 !important; /* Remove qualquer opacidade aplicada */
  color: black !important; /* Define uma cor sólida para o texto */
}

.v-radio .v-input--selection-controls__ripple {
  background-color: transparent !important; /* Remove qualquer efeito de ripple que possa parecer transparente */
}

.v-radio .v-label {
  color: black !important; /* Garante que o texto do rótulo seja visível */
  font-weight: bold; /* Deixa o texto mais destacado */
}
</style>